import React, { useEffect, useState } from "react"
import { Alert, Button, Form, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import { get, put } from "helpers/api_helper"
import toast from "toastr"

const ProjectInstructionModal = ({ project, onClose }) => {
  const [instruction, setInstruction] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [loadFailed, setLoadFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const endpoint = `/Project/${project.id}/Instruction`

  useEffect(() => {
    let active = true
    setLoading(true)
    setError("")
    setLoadFailed(false)
    get(endpoint).then(response => {
      if (!active) return
      if (response?.statusCode === 200) {
        setInstruction(response.data?.projectInstruction ?? "")
      } else {
        setLoadFailed(true)
        setError("Unable to load project instructions. Please retry.")
      }
      setLoading(false)
    }).catch(() => {
      if (!active) return
      setLoadFailed(true)
      setError("Unable to load project instructions. Please retry.")
      setLoading(false)
    })
    return () => { active = false }
  }, [endpoint, attempt])

  const save = async event => {
    event.preventDefault()
    if (loading || loadFailed || saving || instruction.length > 16000) return
    setSaving(true)
    setError("")
    try {
      const response = await put(endpoint, { projectInstruction: instruction })
      if (response?.statusCode !== 200) {
        setError("Unable to save project instructions. Please try again.")
        return
      }
      toast.success("Project instructions saved")
      onClose()
    } catch {
      setError("Unable to save project instructions. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen toggle={saving ? undefined : onClose} size="lg" backdrop="static">
      <Form onSubmit={save}>
        <ModalHeader toggle={saving ? undefined : onClose}>
          {project.name} — Project Instructions
        </ModalHeader>
        <ModalBody>
          {error && <Alert color="danger">{error}</Alert>}
          {loading ? <Spinner size="sm" /> : loadFailed ? (
            <Button color="secondary" onClick={() => setAttempt(value => value + 1)}>Retry</Button>
          ) : (
            <>
              <Label for="projectInstruction">Project README / Instructions</Label>
              <Input
                id="projectInstruction"
                name="projectInstruction"
                type="textarea"
                rows={14}
                maxLength={16000}
                value={instruction}
                disabled={saving}
                onChange={event => setInstruction(event.target.value)}
                placeholder="Add setup steps, working guidelines, and project instructions for developers."
              />
              <small className="text-muted">
                {instruction.length.toLocaleString()} / 16,000 characters. Plain text. Clear the text and save to remove instructions.
              </small>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button color="primary" type="submit" disabled={loading || loadFailed || saving}>
            {saving ? "Saving…" : "Save Instructions"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default ProjectInstructionModal
